/**
 * Authentifizierungs-Service — kapselt Supabase Auth und Edge Functions.
 * Emittiert EventBus-Events bei Auth-Änderungen.
 */
import type { SupabaseClient } from './SupabaseClient';
import type { EventBus } from '../core/EventBus';
import type { AuthUser, AuthResult } from './AuthTypes';
import { lookupPlayerId } from '../world/PlayerWorldService';

export class AuthService {
  constructor(
    private _supabase: SupabaseClient,
    private _events: EventBus,
  ) {}

  /** Prüft ob eine gültige Session existiert (localStorage). */
  async checkSession(): Promise<AuthResult> {
    const { data, error } = await this._supabase.auth.getSession();
    if (error || !data.session) {
      return { user: null, error: null, errorCode: null };
    }
    const u = data.session.user;
    const meta = u.user_metadata as Record<string, string>;
    const playerId = await lookupPlayerId(this._supabase, u.id);
    if (!playerId) {
      return { user: null, error: 'Spieler-Eintrag nicht gefunden', errorCode: null };
    }
    // display_name aus players-Tabelle laden (Auth-Metadaten sind oft veraltet)
    const { data: playerRow } = await this._supabase
      .from('players').select('display_name, account_name').eq('id', playerId).single();
    const dbDisplayName = (playerRow?.display_name as string) || '';
    const dbAccountName = (playerRow?.account_name as string) || meta['account_name'] || '';
    const user: AuthUser = {
      id: u.id,
      playerId,
      email: u.email ?? '',
      accountName: dbAccountName,
      displayName: dbDisplayName || dbAccountName,
    };
    this._events.emit('auth:session-restored', { user });
    return { user, error: null, errorCode: null };
  }

  /** Login über Account-Name + Passwort via Edge Function. */
  async signIn(accountName: string, password: string): Promise<AuthResult> {
    const res = await this._supabase.functions.invoke('auth-login', {
      body: { account_name: accountName, password },
    });
    if (res.error) {
      return { user: null, error: 'Verbindungsfehler', errorCode: null };
    }
    const body = res.data as Record<string, unknown>;
    if (body['error']) {
      return {
        user: null,
        error: body['error'] as string,
        errorCode: (body['code'] as string) ?? null,
      };
    }

    const { error: sessionErr } = await this._supabase.auth.setSession({
      access_token: body['access_token'] as string,
      refresh_token: body['refresh_token'] as string,
    });
    if (sessionErr) {
      return { user: null, error: sessionErr.message, errorCode: null };
    }

    const userInfo = body['user'] as Record<string, string>;
    const playerId = await lookupPlayerId(this._supabase, userInfo['id']);
    if (!playerId) {
      return { user: null, error: 'Spieler-Eintrag nicht gefunden', errorCode: null };
    }
    const user: AuthUser = {
      id: userInfo['id'],
      playerId,
      email: userInfo['email'],
      accountName,
      displayName: userInfo['display_name'] || accountName,
    };
    this._events.emit('auth:signed-in', { user });
    return { user, error: null, errorCode: null };
  }

  /** Registrierung über Edge Function. Gibt Erfolg oder Fehler zurück. */
  async signUp(
    accountName: string,
    email: string,
    password: string,
  ): Promise<{ success: boolean; error: string | null }> {
    const res = await this._supabase.functions.invoke('auth-register', {
      body: { account_name: accountName, email, password },
    });
    if (res.error) {
      return { success: false, error: 'Verbindungsfehler' };
    }
    const body = res.data as Record<string, unknown>;
    if (body['error']) {
      return { success: false, error: body['error'] as string };
    }
    this._events.emit('auth:signed-up', { accountName });
    return { success: true, error: null };
  }

  /** Logout — löscht Session aus localStorage. */
  async signOut(): Promise<void> {
    await this._supabase.auth.signOut();
    this._events.emit('auth:signed-out', null);
  }

  /** Dev-Modus aktivieren (kein Login, keine Spielerdaten). */
  enterDevMode(): void {
    this._events.emit('auth:dev-mode', null);
  }
}
